/**
 * Document-evidence plane (INTAKE-01 §2; INTAKE-03 §1) — the typed document
 * requirements pinned to the session at plan-freeze, and the UPLOAD admission
 * path (E4_UPLOADED).
 *
 * Wire contract (PINNED, DEC-033 — both sides build to the CONTRACT, not to
 * each other; this module is written against the pinned routes and FAILS SOFT
 * on a 404 so a droom deployed ahead of the be degrades honestly, never
 * crashes):
 *
 *   GET  /api/v1/sessions/{id}/document-requirements
 *        → { requirements: [{docType, phase, requirement, allowUpload,
 *          captureModes:[…]}] } — derived from the session's PINNED check plan
 *          joined to DocTypeRegistry metadata; a session with no doc checks →
 *          empty array (200, honest). 404 = the be doesn't ship the read routes
 *          yet (deploy skew) — the step renders the honest "not available here
 *          yet" state.
 *   GET  /api/v1/sessions/{id}/documents
 *        → { documents: [ArtifactResponse…] } — the be's existing
 *          ArtifactResponse shape (kind, docType, origin, evidenceGrade E-ids,
 *          contentType, sniffedMime, sniffVerified, sha256, capturedAt);
 *          metadata ONLY (never bytes). Testmap #16 adds the ADDITIVE side
 *          plane: `captureSides` on a requirement row and `docSide` on an
 *          artefact row — both optional; absence collapses to today's
 *          single-photo behaviour (a row with no side reads FRONT).
 *   POST /api/v1/sessions/{id}/documents      → multipart: docType + file
 *        (+ `docSide` form field only when the registry pinned a multi-side
 *        policy — the wire to an older be stays byte-identical)
 *        201 one ArtifactResponse · 409 that docType slot is already bound
 *        (WRITE-ONCE — treat as success, refresh) · 422 names the problem
 *        (CONSENT_REQUIRED / capture window / sniff-vs-declared mismatch) ·
 *        404 unknown session OR route absent (deploy skew — fail soft).
 *
 * Evidence honesty (INTAKE-03 §1.2, VCIP-REG ruling — veto-bearing):
 *   • `origin` and `evidenceGrade` are SERVER-stamped from the endpoint used —
 *     this client never sends either (a client-supplied origin is a 400 by
 *     contract, INTAKE-01 I2). Everything posted here is an UPLOAD → E4_UPLOADED.
 *   • An uploaded copy NEVER satisfies a V-CIP identity check and is labelled
 *     "Uploaded — lower evidence grade" everywhere it renders — the two grades
 *     must never look identical (INTAKE-08 §A2).
 *   • The sha256 echoed is the SERVER's hash-at-receipt (integrity cue, never a
 *     "verified" claim). The server re-sniffs magic bytes regardless of any
 *     client precheck.
 *
 * Mock mode (no backend): honest fixtures — pinned-requirement rows that
 * exercise all three renderings (upload-permitted, live-only, optional) and
 * LOCAL metadata about the blob; nothing pretends a server bound evidence.
 */

import { API_BASE_URL, ApiError, delay, getSessionToken, isMockMode, request } from './client';

/** Server-stamped collection channel (never client-supplied) — the be's ArtifactOrigin names. */
export type DocumentOrigin = 'LIVE_CAPTURE' | 'SELF_CAPTURE' | 'UPLOAD';

/**
 * Server-stamped evidence grade (the be's EvidenceGrade E-ids, INTAKE-03 §1.2),
 * derived from origin at write. Channel provenance, never an outcome.
 */
export type DocumentEvidenceGrade =
  | 'E1_SOURCE_VERIFIED'
  | 'E2_LIVE_CAPTURED'
  | 'E3_SELF_CAPTURED'
  | 'E4_UPLOADED';

/**
 * One pinned document requirement row (the pinned contract's shape). `docType`,
 * `phase` and `captureModes` stay OPEN strings — the be owns the closed
 * vocabularies (DocType / CheckPhase / ArtifactOrigin registries); unknown
 * values render with a humanised label instead of crashing (deploy-skew
 * tolerance).
 */
export interface DocumentRequirement {
  readonly docType: string;
  /** The check-plan phase the requirement rides (PRE_CALL | IN_CALL | POST_CALL). */
  readonly phase: string;
  readonly requirement: 'OPTIONAL' | 'REQUIRED';
  /** Upload admissibility is a VCIP-REG ruling pinned into the row — never a client default. */
  readonly allowUpload: boolean;
  /** Permitted collection channels (ArtifactOrigin names) — registry metadata; `allowUpload` stays the ruling. */
  readonly captureModes: readonly string[];
  /**
   * Testmap #16 (ADDITIVE): the registry's side policy for this docType —
   * FRONT_ONLY | FRONT_BACK_REQUIRED (passport/DL) | FRONT_BACK_OPTIONAL
   * (Aadhaar/Voter address side). OPEN string (the be registry owns the
   * vocabulary); absent/unknown = FRONT_ONLY — an older be never grows a
   * back side it did not pin (flow/steps/documentSides.ts normalises).
   */
  readonly captureSides?: string | null;
}

/**
 * Document artefact metadata echoed by the backend — the be's ArtifactResponse
 * shape, verbatim (never the bytes). NON_NULL serialisation: `docType`,
 * `origin`, `evidenceGrade` and `sniffedMime` are OMITTED where honestly
 * unknown (legacy rows / older deploys) — absent, never fabricated.
 */
export interface DocumentArtifactMeta {
  /** SessionArtifactKind name — 'DOCUMENT' on the typed document plane. */
  readonly kind: string;
  readonly docType?: string;
  readonly origin?: DocumentOrigin;
  readonly evidenceGrade?: DocumentEvidenceGrade;
  readonly contentType: string;
  readonly sniffedMime?: string;
  readonly sniffVerified?: boolean;
  readonly sha256: string;
  readonly capturedAt: string;
  /**
   * Testmap #16 (ADDITIVE): which side of the document this row is —
   * FRONT | BACK. Absent on pre-side rows and older deploys; absence reads
   * FRONT (that is what every one-photo row was — documentSides.sideOfMeta).
   */
  readonly docSide?: string | null;
}

/**
 * Customer-facing labels for the KNOWN DocType vocabulary — mirrors the be's
 * DocType enum names EXACTLY (INTAKE-01 §2.1; the be registry is the closed
 * vocabulary). Unknown names take the humanised fallback (deploy-skew).
 */
const DOC_TYPE_LABELS: Readonly<Record<string, string>> = {
  PAN_CARD: 'PAN card',
  AADHAAR: 'Aadhaar',
  DRIVING_LICENCE: 'Driving licence',
  VOTER_ID: 'Voter ID',
  PASSPORT: 'Passport',
  UTILITY_BILL: 'Utility bill',
  SELFIE: 'Selfie',
  SELFIE_WITH_DOC: 'Selfie with your document',
  OTHER_DOCUMENT: 'Supporting document',
};

/** Label for a docType — known vocabulary first, humanised fallback for skew. */
export function docTypeLabel(docType: string): string {
  const known = DOC_TYPE_LABELS[docType];
  if (known) return known;
  const words = docType.toLowerCase().split(/[_\s]+/).filter(Boolean).join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Document';
}

/** Upload admission prechecks the CLIENT can honestly run (server re-sniffs). */
export const UPLOAD_ACCEPT_TYPES = ['image/jpeg', 'image/png'] as const;
export const UPLOAD_MAX_BYTES = 8 * 1024 * 1024; // mirrors the be's 8 MB artefact cap

/** The pinned per-session document requirements (empty = none pinned). */
export async function listDocumentRequirements(
  sessionId: string,
): Promise<DocumentRequirement[]> {
  if (isMockMode()) {
    await delay(240);
    // Fixtures exercise all three renderings; clearly local-only. Shapes and
    // vocabularies mirror the pinned contract (be enum names, honest floors).
    return [
      {
        docType: 'UTILITY_BILL',
        phase: 'PRE_CALL',
        requirement: 'REQUIRED',
        allowUpload: true,
        captureModes: ['LIVE_CAPTURE', 'SELF_CAPTURE', 'UPLOAD'],
      },
      // #16: exercises the side-sequenced rendering (front bound → "now the back").
      {
        docType: 'DRIVING_LICENCE',
        phase: 'PRE_CALL',
        requirement: 'REQUIRED',
        allowUpload: true,
        captureModes: ['LIVE_CAPTURE', 'SELF_CAPTURE', 'UPLOAD'],
        captureSides: 'FRONT_BACK_REQUIRED',
      },
      {
        docType: 'OTHER_DOCUMENT',
        phase: 'PRE_CALL',
        requirement: 'OPTIONAL',
        allowUpload: true,
        captureModes: ['LIVE_CAPTURE', 'SELF_CAPTURE', 'UPLOAD'],
      },
      {
        docType: 'PAN_CARD',
        phase: 'IN_CALL',
        requirement: 'REQUIRED',
        allowUpload: false,
        captureModes: ['LIVE_CAPTURE', 'SELF_CAPTURE'],
        captureSides: 'FRONT_ONLY',
      },
    ];
  }
  const res = await request<{ requirements: DocumentRequirement[] | null }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/document-requirements`,
  );
  return res.requirements ?? [];
}

/** Typed document artefacts already bound to this session (resume support). */
export async function listDocuments(sessionId: string): Promise<DocumentArtifactMeta[]> {
  if (isMockMode()) {
    await delay(180);
    return []; // no server evidence store exists — nothing is ever "already bound"
  }
  const res = await request<{ documents: DocumentArtifactMeta[] | null }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/documents`,
  );
  return res.documents ?? [];
}

/**
 * Upload one document file to the UPLOAD admission path. XHR (not fetch) so the
 * progress bar can be DETERMINATE on real bytes (INTAKE-08 §A2 — "progress that
 * never lies"; an indeterminate spinner is the banned alternative).
 * `onProgress` receives 0..1 of bytes actually sent.
 */
export async function uploadDocument(
  sessionId: string,
  docType: string,
  file: Blob,
  fileName: string,
  onProgress: (fraction: number) => void,
  /**
   * Testmap #16 (ADDITIVE): which side this upload is (FRONT | BACK). Sent as a form field only
   * when the registry pinned a multi-side policy; omitted otherwise, so the wire to an older be is
   * byte-identical to today's single-photo POST (a be without the side column ignores nothing —
   * it never sees the field).
   */
  docSide?: 'FRONT' | 'BACK',
): Promise<DocumentArtifactMeta> {
  if (isMockMode()) {
    // Honest local fixture: walk the progress, echo LOCAL metadata (grade is
    // what the server WOULD stamp for this path — nothing claims a server
    // bound it; sniffedMime/sniffVerified stay ABSENT — no server sniff ran).
    for (const f of [0.2, 0.45, 0.7, 0.9, 1]) {
      await delay(140);
      onProgress(f);
    }
    return {
      kind: 'DOCUMENT',
      docType,
      origin: 'UPLOAD',
      evidenceGrade: 'E4_UPLOADED',
      contentType: file.type || 'image/jpeg',
      sha256: (await localSha256Hex(file)) ?? 'mock-sha256-unavailable',
      capturedAt: new Date().toISOString(),
      ...(docSide ? { docSide } : {}),
    };
  }

  const form = new FormData();
  form.append('docType', docType);
  if (docSide) form.append('docSide', docSide);
  form.append('file', file, fileName);

  return new Promise<DocumentArtifactMeta>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/documents`);
    xhr.setRequestHeader('Accept', 'application/json');
    const token = getSessionToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(Math.min(1, e.loaded / e.total));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Network error during upload'));
    xhr.onload = () => {
      const status = xhr.status;
      const text = xhr.responseText ?? '';
      if (status >= 200 && status < 300) {
        try {
          resolve(JSON.parse(text) as DocumentArtifactMeta);
        } catch {
          reject(new ApiError(status, 'Malformed upload response'));
        }
        return;
      }
      // Mirror client.ts error semantics: surface the server's own message
      // (409 write-once / 422 named problem) so callers branch identically.
      // Same RFC7807 order as client.ts errorMessage(), and the same refusal to dump a raw body at
      // a customer (2026-08-25): the be speaks problem+json, so `detail` is the human sentence.
      let message = `POST documents → ${status}`;
      try {
        const parsed = JSON.parse(text) as {
          detail?: unknown; title?: unknown; message?: unknown; error?: unknown;
        };
        if (typeof parsed.detail === 'string' && parsed.detail) message = parsed.detail;
        else if (typeof parsed.message === 'string' && parsed.message) message = parsed.message;
        else if (typeof parsed.error === 'string' && parsed.error) message = parsed.error;
        else if (typeof parsed.title === 'string' && parsed.title) message = parsed.title;
      } catch {
        const plain = text.trim();
        if (plain && !plain.startsWith('{') && !plain.startsWith('[')) message = plain.slice(0, 300);
      }
      reject(new ApiError(status, message));
    };
    xhr.send(form);
  });
}

/** Local SHA-256 (hex) of a blob — mock-mode integrity cue only. */
async function localSha256Hex(blob: Blob): Promise<string | null> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return null;
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}
